/**
 * Composable for accessing URL query parameters.
 *
 * @returns Object with query parameter utilities
 */
export function useQueryParams() {
    /**
     * Get all query parameters as URLSearchParams
     */
    const params = computed<URLSearchParams>(() => {
        return new URLSearchParams(window.location.search);
    });

    /**
     * Get a specific query parameter by key
     *
     * @param key - The query parameter key
     * @returns {string | null} The parameter value or null if not found
     */
    const get = (key: string): string | null => {
        return params.value.get(key);
    };

    /**
     * Get all values for a query parameter key
     *
     * @param key - The query parameter key
     * @returns {string[]} Array of all values for the key
     */
    const getAll = (key: string): string[] => {
        return params.value.getAll(key);
    };

    /**
     * Check if a query parameter exists
     *
     * @param key - The query parameter key
     * @returns {boolean} True if the parameter exists
     */
    const has = (key: string): boolean => {
        return params.value.has(key);
    };

    /**
     * Get query parameters as a plain object
     *
     * @returns {Record<string, string>} Object with all query parameters
     */
    const toObject = (): Record<string, string> => {
        const obj: Record<string, string> = {};
        params.value.forEach((value, key) => {
            obj[key] = value;
        });
        return obj;
    };

    return { params, get, getAll, has, toObject };
}